import DownloadIcon from "../../../static/media/icons/download-icon.svg?react"
import { Button } from "../button"
import Checkbox from "../checkbox"
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../dialog"
import { Input } from "../input"
import { useMemo, useState } from "react"

type ExportLeadsProps = {
  open: boolean
  onClose: () => void
  selectedCount: number
  totalAvailable: number
}

const ExportLeads = ({ open, onClose, selectedCount, totalAvailable }: ExportLeadsProps) => {
  const [exportMode, setExportMode] = useState<"selected" | "custom">("selected")
  const [customCount, setCustomCount] = useState("")
  const [emailSelected, setEmailSelected] = useState(true)
  const [phoneSelected, setPhoneSelected] = useState(true)

  const selectedData: string[] = []
  if (emailSelected) selectedData.push("Emails")
  if (phoneSelected) selectedData.push("Phone Numbers")

  const parsedCustomCount = useMemo(() => {
    const value = Number(customCount)
    if (Number.isNaN(value) || value < 0) return 0
    // clamp to total available
    return Math.min(value, totalAvailable)
  }, [customCount, totalAvailable])

  const exportCount = exportMode === "selected" ? selectedCount : parsedCustomCount

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose()
      }}
    >
      <DialogContent className="max-w-[400px] rounded-xl border p-0">
        <DialogHeader className="flex flex-row items-start justify-between border-b border-border p-5">
          <DialogTitle className="flex flex-row items-center gap-3.5">
            <span className="flex items-center justify-center rounded-full border p-[10px]">
              <DownloadIcon className="size-5 text-gray-600" />
            </span>
            <div className="flex flex-col items-start">
              <span className="text-sm font-medium text-gray-950">Export Leads</span>
              <span className="text-xs font-normal text-gray-600">Download Leads in CSV Format</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-5 p-5">
          {/* Export selected leads */}
          <label className="flex cursor-pointer items-center gap-3">
            <div className="relative">
              <input
                type="radio"
                name="exportMode"
                value="selected"
                checked={exportMode === "selected"}
                onChange={(e) => setExportMode(e.target.value as "selected" | "custom")}
                className="sr-only"
              />
              <div
                className={`size-4 rounded-full border-2 transition-all ${
                  exportMode === "selected" ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-white"
                }`}
              >
                {exportMode === "selected" && <div className="absolute inset-0 m-[3px] rounded-full bg-white" />}
              </div>
            </div>
            <span className="text-sm font-medium text-gray-950">
              Export Selected leads ({selectedCount} {selectedCount === 1 ? "lead" : "leads"})
            </span>
          </label>

          {/* Add custom value */}
          <div className="flex flex-col gap-2">
            <label className="flex cursor-pointer items-center gap-3">
              <div className="relative">
                <input
                  type="radio"
                  name="exportMode"
                  value="custom"
                  checked={exportMode === "custom"}
                  onChange={(e) => setExportMode(e.target.value as "selected" | "custom")}
                  className="sr-only"
                />
                <div
                  className={`size-4 rounded-full border-2 transition-all ${
                    exportMode === "custom" ? "border-blue-600 bg-blue-600" : "border-gray-300 bg-white"
                  }`}
                >
                  {exportMode === "custom" && <div className="absolute inset-0 m-[3px] rounded-full bg-white" />}
                </div>
              </div>
              <span className="text-sm font-medium text-gray-950">Add custom value</span>
            </label>

            <div className="flex flex-row items-center justify-between gap-2">
              <Input
                type="number"
                placeholder="Enter Number"
                value={customCount}
                onChange={(e) => setCustomCount(e.target.value)}
                disabled={exportMode !== "custom"}
                className={`w-full rounded-lg p-[10px] ${exportMode !== "custom" ? "bg-gray-50 opacity-60" : ""}`}
              />
              <span className=" text-xs font-medium text-gray-600">Out of {totalAvailable}</span>
            </div>
          </div>

          <div>
            <span className="text-sm font-medium text-gray-950">Data to export</span>

            <div className="rounded-lg border border-border">
              <div className="flex flex-row gap-[10px] p-3.5">
                <Checkbox checked={emailSelected} onChange={setEmailSelected} />
                <span className="text-sm font-medium text-gray-950">Email Addresses</span>
              </div>
              <div className="border-b"></div>
              <div className="flex flex-row gap-[10px] p-3.5">
                <Checkbox checked={phoneSelected} onChange={setPhoneSelected} />
                <span className="text-sm font-medium text-gray-950">Phone Numbers</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 rounded-2xl border p-4">
            <div className="py-1">
              <span className="text-sm font-medium text-gray-950">Credits Cost</span>
            </div>

            <div className="flex flex-col items-center justify-center rounded-xl border">
              <div className="py-1.5">
                <span className="text-xl font-medium text-gray-950">15 Credits</span>
              </div>
              <div className="border-b"></div>
              <div className="flex w-full justify-center rounded-b-xl bg-[#F7F7F7] py-1.5">
                <span className="text-sm font-medium text-gray-600">
                  Available = <span className="text-gray-950">1000</span>{" "}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex flex-row items-center justify-between gap-2">
                <span className="text-xs font-medium text-gray-600">Total Contacts</span>
                <span className="text-xs font-medium text-gray-950">{exportCount}</span>
              </div>

              {selectedData.length > 0 && (
                <div className="flex flex-row items-center justify-between gap-2">
                  <span className="text-xs font-medium text-gray-600">Data</span>
                  <span className="text-xs font-medium text-gray-950">{selectedData.join(" + ")}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t p-5">
          <DialogClose asChild>
            <Button variant="outline" className="w-full rounded-lg p-2 text-sm font-medium text-[#5C5C5C]">
              Cancel
            </Button>
          </DialogClose>
          <Button variant="outline" className="w-full rounded-lg bg-[#335CFF] p-2 text-sm font-medium text-white hover:bg-[#335CFF] hover:text-white">
            Export
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ExportLeads
